<template>
  <div class="onboarding-app"><!-- container-fluid py-4 -->
<!--    <div class="row">-->
    <OnboardingSidebar
      :steps="stepsList"
      :current-step="state.step"
      :points="state.points"
      :visited="ONBOARDING_VISITED"
      :user-level="userLevel"
      :progress-pct="progressPct"
      :next-step-label="nextStepLabel"
      :check-step-done="isStepDone"
      :is-loading="isGeneratingDocs"
      :docs-generated="!!state.docsGenerated"
      @select-step="setStep"
    />

    <OnboardingMain
      :state-data="state"
      :current-step="state.step"
      :total-steps="STEPS.length"
      :show-footer="!(state.step === 9 && allSigned)"
      :active-insurers="activeInsurers"
      :visited="ONBOARDING_VISITED"
      :is-generating-docs="isGeneratingDocs"
      @update-generating="isGeneratingDocs = $event"
      @prev="prevStep"
      @next="nextStep"
      @select-step="setStep"
    />

  </div>
</template>

<script>

import { STEPS, INSURERS, DOCUMENTS, ONBOARDING_TEMPLATES, COMPLIANCE_TABS, COMPLIANCE, PREP_CHECKLIST, MILESTONES } from './data/onboardingData';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import AsideMenuLogo from '@/widgets/app-main-menu/ui/main-menu/aside-menu-desktop/aside-menu-logo';
import OnboardingSidebar from './components/OnboardingSidebar.vue';
import OnboardingMain from './components/OnboardingMain.vue';

const STORE_KEY = 'uconnect-onboarding-vue';

const DEFAULT_STATE = {
  step: 0,
  form: {},
  compliance: {},
  docType: 'passport',
  noRnokpp: false,
  hasKved: true,
  taxSystem: 'simplified',
  inRegistry: true,
  hasEmployees: false,
  hasSalesHead: false,
  uploaded: {},
  insurersOff: {},
  contractRead: false,
  signedContract: false,
  signedDocs: {},
  signedForms: {},
  complianceTab: 0,
  openGroup: null,
  points: 0,
  visited: { 0: true },
};

export default {
  name: 'OnboardingWizard',
  components: {
    AsideMenuLogo,
    OnboardingSidebar,
    OnboardingMain,
  },

  data() {
    return {
      STEPS,
      INSURERS,
      DOCUMENTS,
      COMPLIANCE_TABS,
      COMPLIANCE,
      PREP_CHECKLIST,
      MILESTONES,
      isGeneratingDocs: false,
      stateDefault: {
        step: 0,
        form: {},
        compliance: {},
        docType: 'passport',
        noRnokpp: false,
        hasKved: true,
        taxSystem: 'simplified',
        inRegistry: true,
        hasEmployees: false,
        hasSalesHead: false,
        uploaded: {},
        insurersOff: {},
        contractRead: false,
        signedContract: false,
        signedDocs: {},
        signedForms: {},
        complianceTab: 0,
        openGroup: null,
        points: 0,
        visited: { 0: true },
      },
      toast: {
        show: false,
        text: '',
        icon: '✨',
        timer: null,
      }
    };
  },
  computed: {
    ...mapGetters({
      state: 'ONBOARDING_STATE',
    }),
    ...mapGetters(['ONBOARDING_VISITED', 'ONBOARDING_POINTS', 'ONBOARDING_FORM',]),
    activeInsurers() {
      return this.INSURERS.filter(i => !this.state.insurersOff[i.code]);
    },
    stepsList() {
      return this.STEPS.filter(i => !i.optional);
    },
    nextStepLabel() {
      const next = this.STEPS[this.state.step + 1];
      return next ? next.label : null;
    },
    completedStepsCount() {
      return this.STEPS.reduce((n, _, i) => n + (this.isStepDone(i) ? 1 : 0), 0);
    },
    progressPct() {
      return Math.round((this.completedStepsCount / this.STEPS.length) * 100);
    },
    userLevel() {
      if (this.completedStepsCount < 4) return 1;
      if (this.completedStepsCount < 8) return 2;
      return 3;
    },
    allSigned() {
      const signedDocs = this.DOCUMENTS.filter(d => this.state.signedDocs[d.key]).length;
      const signedForms = this.activeInsurers.filter(i => this.state.signedForms[i.code]).length;
      const total = 1 + this.DOCUMENTS.length + this.activeInsurers.length;
      const signed = (this.state.signedContract ? 1 : 0) + signedDocs + signedForms;
      return total > 0 && signed === total;
    },
  },
  created() {
    this.INIT_ONBOARDING();
  },
  watch: {
    state: {
      deep: true,
      handler(newVal) {
        this.checkDataChanges(newVal);
      }
    }
  },
  methods: {
    ...mapActions(['INIT_ONBOARDING', 'SAVE_ONBOARDING', 'SET_STEP']),
    ...mapMutations(['ADD_POINTS']),
    async setStep(idx) {
      if (this.isGeneratingDocs) return;

      if (idx === 9 && !this.state.docsGenerated) {
        if (this.$message) {
          this.$message.warning('Спочатку необхідно сформувати пакет документів на кроці «Перевірка даних»!');
        }
        return;
      }

      try {
        this.SET_STEP(idx);
      } catch (error) {
        console.error('Помилка збереження:', error);
      }

      window.scrollTo({ top: 0 });
    },
    nextStep() {
      if (this.isGeneratingDocs) {
        return;
      }

      if (this.state.step === 8 && !this.state.docsGenerated) {
        if (this.$message) {
          this.$message.warning('Спочатку необхідно сформувати пакет документів!');
        }
        return;
      }

      if (this.state.step < this.STEPS.length - 1) {
        const next = this.state.step + 1;
        this.setStep(next);

        this.ADD_POINTS(50);
        if ([3, 7, 9].includes(next)) {
          this.triggerToast('Етап пройдено! +50 XP', '🎯');
        }
      }
    },
    prevStep() {
      if (this.isGeneratingDocs) return;
      if (this.state.step > 0) {
        this.setStep(this.state.step - 1);
      }
    },
    getCurrentDataHash(d) {
      if (!d) return '';
      return JSON.stringify({
        form: d.form,
        compliance: d.compliance,
        uploaded: d.uploaded,
        insurersOff: d.insurersOff,
        hasEmployees: d.hasEmployees,
        hasSalesHead: d.hasSalesHead,
        docType: d.docType,
        taxSystem: d.taxSystem,
        inRegistry: d.inRegistry,
        noRnokpp: d.noRnokpp,
        hasKved: d.hasKved
      });
    },
    checkDataChanges(stateObj) {
      if (!stateObj || !stateObj.docsGenerated || !stateObj.lastGeneratedHash) {
        return;
      }
      const currentHash = this.getCurrentDataHash(stateObj);
      if (currentHash !== stateObj.lastGeneratedHash) { // && this.state.step > 0
        this.$set(stateObj, 'docsGenerated', false);
        if (this.$message) {
          this.$message.warning('Дані було змінено. Необхідно повторно сформувати пакет документів на кроці «Перевірка даних».');
        }
        this.SAVE_ONBOARDING();
      }
    },

    triggerToast(text, icon = '✨') {
      this.toast.icon = icon;
      this.toast.text = text;
      this.toast.show = true;
      clearTimeout(this.toast.timer);
      this.toast.timer = setTimeout(() => {
        this.toast.show = false;
      }, 2200);
    },
    isStepDone(i) {
      const form = this.state.form || {};

      switch (i) {
        case 0: return this.state.step > 0;
        case 1: return !!(form.last_name && form.first_name && form.phone && form.email);
        case 2: return !!(form.iban && form.bank_name && form.payee_name);
        case 3: return this.COMPLIANCE.every(q => this.state.compliance && (this.state.compliance[q.key] === true || this.state.compliance[q.key] === false));
        case 4: return (this.state.hasEmployees !== null && this.state.hasEmployees !== undefined && this.state.hasSalesHead !== null && this.state.hasSalesHead !== undefined);
        case 5: return this.DOCUMENTS.filter((doc, index) => {
          if(this.state.noRnokpp) {
            return doc.key !== 'qualification' && doc.key !== 'ipn';
          } else {
            return doc.key !== 'qualification';
          }
        }).every(d => this.state.uploaded && this.state.uploaded[d.key]);

        case 6: return this.activeInsurers.length > 0 && !!this.state.insurersDone;
        case 7: return !!this.state.contractRead;
        case 8: return !!this.state.docsGenerated;
        case 9: return this.allSigned;
      }
      return false;
    },
  }
};
</script>

<style lang="scss">
@import "./styles/onboardingStyles_grouped";
</style>
